import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import { FileClock, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePurchaseProofHistoryColumns } from '@client/components/invoices/purchase-proof-history-columns.tsx';
import { getUserPurchaseProofsQueryKey, userPurchaseProofs } from '@r/api/generated/client.ts';
import type {
  PurchaseProofPurchaseProofCollectionReadModel,
  UserPurchaseProofsParams
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { Button } from '@ui/button.tsx';
import { Link } from '@tanstack/react-router';

const MAX_PAGE_SIZE = 30;
const PURCHASE_PROOF_FILTER_FIELDS: DataTableFilterField<PurchaseProofPurchaseProofCollectionReadModel>[] =
  [];

/** Displays the paginated purchase-proof history for the current user's company. */
export function PurchaseProofHistory() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [purchaseProofs, setPurchaseProofs] = useState<
    PurchaseProofPurchaseProofCollectionReadModel[]
  >([]);
  const [pageCount, setPageCount] = useState(1);

  const { table } = useDataTable({
    data: purchaseProofs,
    columns: usePurchaseProofHistoryColumns(),
    pageCount,
    filterFields: PURCHASE_PROOF_FILTER_FIELDS
  });
  const { pageIndex, pageSize } = table.getState().pagination as PaginationState;
  const params: UserPurchaseProofsParams = {
    page: pageIndex + 1,
    perPage: Math.min(pageSize, MAX_PAGE_SIZE)
  };

  const purchaseProofsQuery = useQuery({
    queryKey: getUserPurchaseProofsQueryKey(params),
    queryFn: async () => {
      try {
        return await userPurchaseProofs(params);
      } catch (error) {
        if (isOrvalErrorFromPath(error, 'get', '/purchase_proofs')) {
          showOrvalErrorToast(error, t('toast.error.label'));
        } else {
          toast.error(t('toast.error.label'));
        }
        throw error;
      }
    },
    placeholderData: (previousData) => previousData
  });

  useEffect(() => {
    if (purchaseProofsQuery.data) {
      setPurchaseProofs(purchaseProofsQuery.data.data ?? []);
      setPageCount(purchaseProofsQuery.data.pagination?.last ?? 1);
    }
  }, [purchaseProofsQuery.data]);

  useEffect(() => {
    if (pageSize > MAX_PAGE_SIZE) {
      table.setPageSize(MAX_PAGE_SIZE);
    }
  }, [pageSize, table]);

  return (
    <div className="min-w-0 flex-grow overflow-x-hidden lg:pl-8">
      <div className="mb-10">
        <div className="mb-3 flex items-center">
          <FileClock className="mr-3 shrink-0" aria-hidden />
          <h1 className="font-bold text-2xl text-foreground md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
            {t('invoices.history.title')}
          </h1>
        </div>
        <p className="text-muted-foreground">{t('invoices.history.description')}</p>
      </div>

      {purchaseProofsQuery.isLoading && (
        <DataTableSkeleton columnCount={4} cellWidths={['18rem', '24rem', '10rem', '10rem']} />
      )}

      {purchaseProofsQuery.isError && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">{t('invoices.history.error')}</p>
          <Button variant="outline" onClick={() => void purchaseProofsQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {purchaseProofsQuery.isSuccess && (
        <DataTable table={table} totalItems={purchaseProofsQuery.data.pagination?.totalItems} />
      )}

      <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
        <span className="font-medium">{t('invoices.history.submit.question')}</span>
        <Button asChild rounded="full">
          <Link to="/invoices">
            <Upload aria-hidden />
            {t('invoices.history.submit')}
          </Link>
        </Button>
      </div>
    </div>
  );
}
